import { z } from "zod";

/**
 * The attachment shape webapp FORMS hold as a field value.
 *
 * Deliberately structural and flat: it must never reference a recursive
 * attachment resource shape whose relation edges make TanStack Form's DeepKeys
 * hit TS2589 (verified with a scratch harness).
 */

export const attachmentUploadStatusSchema = z.enum([
  "added",
  "uploading",
  "done",
  "error",
]);

export type AttachmentUploadStatus = z.infer<
  typeof attachmentUploadStatusSchema
>;

export const formAttachmentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  fileSize: z.number(),
  status: attachmentUploadStatusSchema,
  originalContentUri: z.string().optional(),
  contentUri: z.string().optional(),
  thumbnailUri: z.string().optional(),
  percent: z.number().optional(),
  uid: z.string().optional(),
  createdAt: z.string().optional(),
  // MIME type — the lightbox's extension-resolution fallback for stored
  // attachments whose URI has no recognizable extension.
  contentType: z.string().optional(),
});

export type FormAttachment = z.infer<typeof formAttachmentSchema>;

/**
 * The canonical `attachments` field schema. The refinement is what makes
 * `canSubmit` false while an upload is in flight, so consumers no longer
 * hand-write an upload gate on their submit button.
 *
 * Compose off `formAttachmentSchema` directly if a form needs a different rule.
 */
export const formAttachmentsSchema = z
  .array(formAttachmentSchema)
  .refine((items) => !areAttachmentsPending(items), {
    message: "Files are still uploading.",
  });

/**
 * Anything attachment-shaped that has NOT been through {@link toFormAttachment}:
 * a REST v5 attachment row or a persisted zustand blob. Renderers accept this;
 * form values are always `FormAttachment`.
 */
export type AttachmentSource = {
  id?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
  originalContentUri?: string | null;
  contentUri?: string | null;
  thumbnailUri?: string | null;
  createdAt?: string | Date | null;
  status?: string | null;
  percent?: number | null;
  uid?: string | null;
  contentType?: string | null;
};

/**
 * A source with no status is a stored, fully-uploaded attachment — this single
 * decision used to be re-made (inconsistently) at ~20 call sites, half of which
 * wrote `status !== "done"` and half `status && status !== "done"`.
 */
export function toAttachmentUploadStatus(raw: unknown): AttachmentUploadStatus {
  const parsed = attachmentUploadStatusSchema.safeParse(raw);
  return parsed.success ? parsed.data : "done";
}

function toIsoString(
  value: string | Date | null | undefined,
): string | undefined {
  if (!value) return undefined;
  return value instanceof Date ? value.toISOString() : value;
}

export function toFormAttachment(source: AttachmentSource): FormAttachment {
  const original = source.originalContentUri ?? source.contentUri ?? undefined;
  const content = source.contentUri ?? source.originalContentUri ?? undefined;
  return {
    id: source.id ?? "",
    fileName: source.fileName ?? "",
    fileSize: source.fileSize ?? 0,
    status: toAttachmentUploadStatus(source.status),
    originalContentUri: original ?? undefined,
    contentUri: content ?? undefined,
    thumbnailUri: source.thumbnailUri ?? undefined,
    percent: source.percent ?? undefined,
    uid: source.uid ?? undefined,
    createdAt: toIsoString(source.createdAt),
    contentType: source.contentType ?? undefined,
  };
}

export function toFormAttachments(
  sources: AttachmentSource[] | null | undefined,
): FormAttachment[] {
  return (sources ?? []).map(toFormAttachment);
}

/** True while any attachment has not finished uploading (`status !== "done"`). */
export function areAttachmentsPending(
  attachments: readonly { status: AttachmentUploadStatus }[] | null | undefined,
): boolean {
  return (attachments ?? []).some((attachment) => attachment.status !== "done");
}
